import React, { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download, ArrowRight, Shield, ZapOff, Smartphone, Mail } from 'lucide-react';
import { trackClick } from '../../services/landingAnalytics';

export const HeroSection = ({ onInstallClick }) => {
  const navigate = useNavigate();
  const heroRef = useRef(null);
  const [cursor, setCursor] = useState({ x: 50, y: 50, visible: false });

  useEffect(() => {
    const el = heroRef.current;
    if (!el) return;
    const handleMove = (e) => {
      const rect = el.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      setCursor({ x, y, visible: true });
    };
    const handleLeave = () => setCursor((c) => ({ ...c, visible: false }));
    el.addEventListener('mousemove', handleMove);
    el.addEventListener('mouseleave', handleLeave);
    return () => {
      el.removeEventListener('mousemove', handleMove);
      el.removeEventListener('mouseleave', handleLeave);
    };
  }, []);

  return (
    <section
      ref={heroRef}
      className="relative pt-28 pb-20 lg:pt-36 lg:pb-28 overflow-hidden bg-gradient-to-br from-[#F8FAFC] via-white to-[#F0FDFA]"
      data-testid="hero-section"
    >
      {/* Animated grid pattern that brightens around cursor */}
      <div
        className="absolute inset-0 pointer-events-none transition-opacity duration-300"
        style={{
          backgroundImage:
            'linear-gradient(rgba(15,23,42,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(15,23,42,0.5) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
          maskImage: cursor.visible
            ? `radial-gradient(circle 280px at ${cursor.x}% ${cursor.y}%, rgba(0,0,0,0.85), rgba(0,0,0,0.04) 70%)`
            : 'radial-gradient(circle at 50% 50%, rgba(0,0,0,0.05), rgba(0,0,0,0.02))',
          WebkitMaskImage: cursor.visible
            ? `radial-gradient(circle 280px at ${cursor.x}% ${cursor.y}%, rgba(0,0,0,0.85), rgba(0,0,0,0.04) 70%)`
            : 'radial-gradient(circle at 50% 50%, rgba(0,0,0,0.05), rgba(0,0,0,0.02))',
        }}
        data-testid="hero-grid-bg"
      />
      {/* Cursor-following teal halo */}
      <div
        className="absolute pointer-events-none rounded-full transition-opacity duration-300"
        style={{
          left: `${cursor.x}%`,
          top: `${cursor.y}%`,
          width: '420px',
          height: '420px',
          transform: 'translate(-50%, -50%)',
          background:
            'radial-gradient(circle, rgba(20,212,198,0.18) 0%, rgba(20,212,198,0.05) 40%, transparent 70%)',
          opacity: cursor.visible ? 1 : 0,
          filter: 'blur(8px)',
        }}
        data-testid="hero-cursor-halo"
      />
      {/* Top-right ambient blob */}
      <div className="absolute -top-32 -right-32 w-[480px] h-[480px] bg-[#14D4C6]/15 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* Left: Text */}
          <div className="lg:col-span-7">
            <div
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 shadow-sm mb-6"
              data-testid="hero-badge"
            >
              <Shield className="w-3.5 h-3.5 text-[#14D4C6]" />
              <span className="text-xs font-medium text-slate-600 tracking-wide">
                Multi-tenant PWA · Live ANPR · Push-notified
              </span>
            </div>

            <h1
              className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-bold text-slate-900 leading-[1.05]"
              style={{ fontFamily: 'Outfit, sans-serif' }}
              data-testid="hero-headline"
            >
              Smarter security.{' '}
              <span className="relative inline-block">
                <span className="relative z-10">Simpler society life.</span>
                <span className="absolute inset-x-0 bottom-1 h-3 bg-[#14D4C6]/30 -z-0 rounded-sm" />
              </span>
            </h1>

            <p
              className="mt-6 text-lg text-slate-600 max-w-xl leading-relaxed"
              style={{ fontFamily: 'Work Sans, sans-serif' }}
              data-testid="hero-subhead"
            >
              GateKeeper Pro brings your gate, your residents, and your management
              committee onto one calm, real-time platform — with ANPR vehicle
              recognition, QR gate passes, surveys, notices, chat and more.
            </p>

            {/* USP chips: no ads + any device + email OTP */}
            <div className="mt-6 flex flex-wrap gap-2.5" data-testid="hero-usp-chips">
              <UspChip
                icon={<ZapOff className="w-3.5 h-3.5" />}
                label="No ads. Ever."
                testid="usp-no-ads"
              />
              <UspChip
                icon={<Smartphone className="w-3.5 h-3.5" />}
                label="Any device — phone, tablet, desktop"
                testid="usp-any-device"
              />
              <UspChip
                icon={<Mail className="w-3.5 h-3.5" />}
                label="Email + OTP login, not tied to a phone"
                testid="usp-email-otp"
              />
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <button
                onClick={() => {
                  trackClick('hero-install', 'hero');
                  onInstallClick?.();
                }}
                className="group inline-flex items-center gap-2 bg-[#14D4C6] hover:bg-[#10B5A9] text-slate-900 font-semibold px-7 py-3.5 rounded-md transition-all shadow-md hover:shadow-lg"
                data-testid="hero-install-btn"
              >
                <Download className="w-4 h-4" />
                Install App
                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </button>
              <button
                onClick={() => {
                  trackClick('hero-login', 'hero');
                  navigate('/login');
                }}
                className="inline-flex items-center gap-2 bg-white hover:bg-slate-50 text-slate-900 font-semibold px-7 py-3.5 rounded-md transition-all border border-slate-200"
                data-testid="hero-login-btn"
              >
                Try Demo / Login
              </button>
            </div>

            <p className="mt-5 text-xs text-slate-500" data-testid="hero-microcopy">
              No download stores. Works as a native app on iOS & Android via PWA.
            </p>
          </div>

          {/* Right: Mockup */}
          <div className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-br from-[#14D4C6]/20 to-blue-500/10 rounded-3xl blur-2xl" />
              <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-slate-200 bg-white">
                <img
                  src="/landing/hero-mockup.jpg"
                  alt="GateKeeper Pro dashboard"
                  className="w-full h-auto block"
                  data-testid="hero-mockup-img"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              </div>
              {/* Floating badge */}
              <div
                className="absolute -bottom-4 -left-4 bg-white rounded-xl shadow-xl border border-slate-100 px-4 py-3 flex items-center gap-2.5"
                data-testid="hero-floating-badge"
              >
                <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                <span className="text-xs font-semibold text-slate-700">
                  ANPR live
                </span>
                <span className="text-xs text-slate-400">· just now</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const UspChip = ({ icon, label, testid }) => (
  <span
    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/90 backdrop-blur-sm border border-slate-200 shadow-sm text-xs font-medium text-slate-700"
    data-testid={testid}
  >
    <span className="text-[#14D4C6]">{icon}</span>
    {label}
  </span>
);

